import { useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import type { TripInstance } from "../api/types";
import { api } from "../api/client";
import { usePollingQuery } from "../hooks/usePollingQuery";
import { useAuth } from "../auth/AuthContext";
import FleetMap from "../components/FleetMap";
import type { LayoutOutletContext } from "../components/Layout";
import type { LiveVehicleRow } from "../components/liveFleet";

const NO_ROWS: ReadonlyMap<string, LiveVehicleRow> = new Map();

function fmtDelay(seconds?: number | null): { text: string; tone: "ok" | "warn" | "crit" | "early" } {
  if (seconds == null) return { text: "on time", tone: "ok" };
  const abs = Math.abs(seconds);
  const mins = Math.round(abs / 60);
  if (abs < 60) return { text: "on time", tone: "ok" };
  if (seconds < 0) return { text: `−${mins} min`, tone: "early" };
  if (abs > 300) return { text: `+${mins} min`, tone: "crit" };
  return { text: `+${mins} min`, tone: "warn" };
}

function driverInitials(name?: string): string {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.replace(/[^a-zA-Z]/g, "").slice(0, 2).toUpperCase() || "??";
}

const AVATAR_COLORS = ["#2CA79A", "#0B69C7", "#F0960B", "#1A8043", "#D0342C", "#8B5CF6", "#E05E3A", "#0891B2"];

function todayLabel(): string {
  return new Date().toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtTime(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

function timeAgo(iso: string): string {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
}

function severityDot(severity: string): string {
  switch (severity) {
    case "SEVERE": return "sev-crit";
    case "WARNING": return "sev-warn";
    default: return "sev-info";
  }
}

export default function DashboardPage() {
  const ctx = useOutletContext<LayoutOutletContext | undefined>();
  const { session } = useAuth();

  const liveTrips = ctx?.activeTrips ?? [];
  const alerts = ctx?.alerts ?? [];
  const activeAlerts = alerts.filter(a => a.is_active);
  const critAlerts = activeAlerts.filter(a => a.severity === "SEVERE");

  const tripCount = liveTrips.length;

  // Scheduled trips for today — drives "Next departure" card
  const scheduledQuery = usePollingQuery<TripInstance[]>(
    () => api.listTripInstances({ status: "scheduled", service_date: todayISO() })
      .then((d) => (Array.isArray(d) ? d : [])),
    [session?.accessToken],
    { intervalMs: 15000 }
  );

  const nextDepartures = useMemo(() => {
    const now = Date.now();
    return (scheduledQuery.data ?? [])
      .filter(t => t.scheduled_start && new Date(t.scheduled_start).getTime() > now)
      .sort((a, b) => new Date(a.scheduled_start!).getTime() - new Date(b.scheduled_start!).getTime())
      .slice(0, 3);
  }, [scheduledQuery.data]);

  const nextTrip = nextDepartures[0];

  // The same on-duty count as the top bar and the live map, from the same
  // rows. This card used to count positions that were not signed out — 0/3
  // on a morning the top bar beside it said 3/35.
  const statsKnown = !!ctx?.statsKnown;
  const onDuty = statsKnown ? ctx!.fleetStats.onDuty : null;

  // On-time performance: the live map's own counters, so the two cannot
  // disagree. This used to re-bucket the trip list itself, counting a trip
  // whose bus had gone dark by its last delay — LATE here while the map
  // beside it said LOST. Null until the fleet is known.
  const fleetStats = ctx?.fleetStats;
  const perf = useMemo(() => {
    if (!statsKnown || !fleetStats) return null;
    const { onTime, late, early } = fleetStats;
    const total = onTime + late + early;
    // No trip running means no measurement, not 0% on time.
    const pct = total > 0 ? Math.round((onTime / total) * 100) : null;
    return { onTime, late, early, total, pct };
  }, [statsKnown, fleetStats]);

  return (
    <div className="dash">
      {/* ── Page header ───────────────────────────────────────────────── */}
      <header className="dash-header">
        <div>
          <h1 className="dash-title">Operations Dashboard</h1>
          <p className="dash-subtitle">
            Monitor your fleet, trips and incidents in real time.
          </p>
        </div>
        <div className="dash-header-actions">
          <button className="btn primary dash-pill">+ New trip</button>
          <button className="btn ghost dash-pill">Publish alert</button>
        </div>
      </header>

      {/* ── Stat cards ────────────────────────────────────────────────── */}
      <div className="dash-stats">
        <div className="dash-stat dash-stat--hero">
          <div className="dash-stat-top">
            <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="6" width="18" height="12" rx="2" />
              <circle cx="7.5" cy="18" r="1.5" />
              <circle cx="16.5" cy="18" r="1.5" />
              <path d="M3 10h18" />
            </svg>
            <span>Buses on duty</span>
          </div>
          <div className="dash-stat-mid">
            <div className="dash-stat-val">
              <span className="dash-big mono">{onDuty ?? "—"}</span>
              <span className="dash-big-sub mono">/{statsKnown ? ctx!.fleetStats.total : "—"}</span>
            </div>
            <svg className="dash-spark" viewBox="0 0 100 32" preserveAspectRatio="none">
              <polyline points="0,26 20,22 40,24 60,16 80,10 100,4" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="100" cy="4" r="3" fill="#fff" />
            </svg>
          </div>
          <span className="dash-stat-foot dash-stat-foot--hero">
            {statsKnown ? `${ctx!.fleetStats.onTime + ctx!.fleetStats.late + ctx!.fleetStats.early + ctx!.fleetStats.lost} on a trip` : "Connecting…"}
          </span>
        </div>

        <div className="dash-stat">
          <div className="dash-stat-top">
            <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M18 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M8 17h6a3 3 0 0 0 3-3V9" />
            </svg>
            <span>Active trips</span>
          </div>
          <div className="dash-stat-mid">
            <div className="dash-stat-val">
              <span className="dash-big mono">{tripCount}</span>
            </div>
            <svg className="dash-spark" viewBox="0 0 100 32" preserveAspectRatio="none">
              <polyline points="0,28 25,20 50,22 75,14 100,6" fill="none" stroke="var(--brand-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="100" cy="6" r="3" fill="var(--brand-primary)" />
            </svg>
          </div>
          <span className="dash-stat-foot">{tripCount} in progress</span>
        </div>

        <div className="dash-stat">
          <div className="dash-stat-top">
            <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3l10 18H2L12 3z M12 10v5 M12 18h0" />
            </svg>
            <span>Warnings</span>
          </div>
          <div className="dash-stat-mid">
            <div className="dash-stat-val">
              <span className="dash-big mono dash-warn">
                {perf?.late ?? "—"}
              </span>
            </div>
            <svg className="dash-spark" viewBox="0 0 100 32" preserveAspectRatio="none">
              <polyline points="0,20 30,14 55,20 80,12 100,8" fill="none" stroke="var(--sev-warn)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="100" cy="8" r="3" fill="var(--sev-warn)" />
            </svg>
          </div>
          <span className="dash-stat-foot">
            {perf ? `${perf.late} late trips` : "—"}
          </span>
        </div>

        <div className="dash-stat">
          <div className="dash-stat-top">
            <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v4l3 2" />
            </svg>
            <span>Open incidents</span>
          </div>
          <div className="dash-stat-mid">
            <div className="dash-stat-val">
              <span className="dash-big mono">{activeAlerts.length}</span>
            </div>
            <svg className="dash-spark" viewBox="0 0 100 32" preserveAspectRatio="none">
              <polyline points="0,18 25,24 50,12 75,18 100,6" fill="none" stroke="var(--sev-info)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="100" cy="6" r="3" fill="var(--sev-info)" />
            </svg>
          </div>
          <span className="dash-stat-foot">{critAlerts.length} critical</span>
        </div>
      </div>

      {/* ── Bottom grid ───────────────────────────────────────────────── */}
      <div className="dash-bottom">
        {/* ── LEFT column ─────────────────────────────────────────────── */}
        <div className="dash-left">
          {/* Live fleet map */}
          <div className="card dash-card">
            <div className="dash-card-head">
              <h2 className="dash-card-title">
                Live fleet map
                <span className="dash-live-dot" />
                <span className="tag sev-ok">LIVE</span>
              </h2>
              <a className="link" href="#/map">Open full map &rarr;</a>
            </div>
            <div className="dash-map-placeholder">
              <FleetMap
                rows={ctx?.liveRows ?? NO_ROWS}
                liveKnown={!!ctx?.liveKnown}
                panicVehicleIds={ctx?.panicVehicleIds}
                theme={ctx?.theme}
              />
            </div>
          </div>

          {/* Live trip board — real data */}
          <div className="card dash-card">
            <div className="dash-card-head">
              <h2 className="dash-card-title">
                Live trip board
                <span className="dash-card-meta">{tripCount} running &middot; {todayLabel()}</span>
              </h2>
              <a className="link" href="#/trips">View all trips &rarr;</a>
            </div>
            <div className="dash-trip-board">
              <div className="dash-tb-header">
                <span className="dash-tb-col dash-tb-col--driver">DRIVER</span>
                <span className="dash-tb-col dash-tb-col--route">ROUTE</span>
                <span className="dash-tb-col dash-tb-col--progress">PROGRESS</span>
                <span className="dash-tb-col dash-tb-col--status">STATUS</span>
                <span className="dash-tb-col dash-tb-col--delay">DELAY</span>
              </div>
              {liveTrips.length === 0 && (
                <div className="dash-tb-empty">No in-progress trips.</div>
              )}
              {liveTrips.slice(0, 5).map((t, i) => {
                const delay = fmtDelay(t.delay_seconds);
                const totalStops = t.stop_times?.length ?? 0;
                const curr = t.current_stop_sequence ?? 0;
                const progress = totalStops > 0 ? Math.round((curr / totalStops) * 100) : 0;
                const initials = driverInitials(t.driver_name);
                const avatarColor = AVATAR_COLORS[i % AVATAR_COLORS.length];
                const routeLabel = t.route_short_name || t.headsign || t.route_long_name || "—";
                const routeColor = t.route_color ? `#${t.route_color}` : "var(--text-4)";
                const startTime = fmtTime(t.scheduled_start);
                const endTime = fmtTime(t.scheduled_end);
                const barColor = delay.tone === "crit" ? "var(--sev-crit)" : delay.tone === "warn" ? "var(--sev-warn)" : "var(--brand-primary)";

                return (
                  <div className="dash-tb-row" key={t.id}>
                    <div className="dash-tb-col dash-tb-col--driver">
                      <span className="dash-tb-avatar" style={{ borderColor: avatarColor, color: avatarColor }}>
                        {initials}
                      </span>
                      <div className="dash-tb-driver-info">
                        <span className="dash-tb-driver-name">{t.driver_name || "Unassigned"}</span>
                        <span className="dash-tb-driver-plate">{t.vehicle_label || "—"}</span>
                      </div>
                    </div>
                    <div className="dash-tb-col dash-tb-col--route">
                      <span className="dash-tb-route-badge" style={{ borderColor: routeColor, color: routeColor }}>
                        {routeLabel}
                      </span>
                    </div>
                    <div className="dash-tb-col dash-tb-col--progress">
                      <span className="dash-tb-time mono">{startTime}</span>
                      <div className="dash-tb-bar-wrap">
                        <div className="dash-tb-bar">
                          <div className="dash-tb-bar-fill" style={{ width: `${progress}%`, background: barColor }} />
                        </div>
                        <span className="dash-tb-stops">{curr}/{totalStops} stops</span>
                      </div>
                      <span className="dash-tb-time mono">{endTime}</span>
                    </div>
                    <div className="dash-tb-col dash-tb-col--status">
                      <span className="dash-tb-status-dot" />
                      <span className="dash-tb-status-text">In progress</span>
                    </div>
                    <div className="dash-tb-col dash-tb-col--delay">
                      <span className={`dash-tb-delay dash-tb-delay--${delay.tone}`}>{delay.text}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── RIGHT column ────────────────────────────────────────────── */}
        <div className="dash-right">
          {/* On-time performance — dynamic */}
          <div className="card dash-card">
            <div className="dash-card-head">
              <h2 className="dash-card-title">On-time performance</h2>
              <span style={{ fontSize: 12, color: "var(--text-4)" }}>Today</span>
            </div>
            <div className="dash-perf-body">
              <div className="dash-donut-wrap">
                <svg viewBox="0 0 120 120" className="dash-donut">
                  <circle cx="60" cy="60" r="52" fill="none" stroke="var(--bg-3)" strokeWidth="10" />
                  {perf?.pct != null && (
                    <circle cx="60" cy="60" r="52" fill="none" stroke="var(--brand-primary)" strokeWidth="10"
                      strokeDasharray={`${(perf.pct / 100) * 2 * Math.PI * 52} ${2 * Math.PI * 52}`}
                      strokeLinecap="round" transform="rotate(-90 60 60)" />
                  )}
                </svg>
                <div className="dash-donut-label">
                  <span className="dash-donut-pct mono">{perf?.pct != null ? `${perf.pct}%` : "—"}</span>
                  <span className="dash-donut-sub">{perf == null ? "connecting" : perf.pct != null ? "on time" : "no trip running"}</span>
                </div>
              </div>
              <div className="dash-legend">
                <div className="dash-legend-row"><span className="dot sev-ok" /><span className="dash-legend-label">On time</span><strong>{perf?.onTime ?? "—"}</strong></div>
                <div className="dash-legend-row"><span className="dot sev-warn" /><span className="dash-legend-label">Late &gt;5 min</span><strong>{perf?.late ?? "—"}</strong></div>
                <div className="dash-legend-row"><span className="dot sev-info" /><span className="dash-legend-label">Early &gt;1 min</span><strong>{perf?.early ?? "—"}</strong></div>
              </div>
            </div>
          </div>

          {/* Next departure — dynamic */}
          <div className="card dash-card dash-card--dark">
            <div className="dash-card-head">
              <h2 className="dash-card-title">
                Next departure{nextTrip ? <> &middot; {nextTrip.route_short_name || nextTrip.headsign || "—"}</> : ""}
              </h2>
              {nextTrip?.route_color && (
                <span className="pill" style={{ background: `#${nextTrip.route_color}`, color: `#${nextTrip.route_text_color || "FFFFFF"}` }}>
                  {nextTrip.route_long_name || nextTrip.route_short_name || ""}
                </span>
              )}
            </div>
            {nextTrip ? (
              <>
                <div className="dash-next-big mono">{fmtTime(nextTrip.scheduled_start)}</div>
                <p className="dash-next-sub">
                  {nextTrip.vehicle_label || "—"} &middot; departs {fmtTime(nextTrip.scheduled_start)} &middot; {nextTrip.headsign || nextTrip.route_short_name || ""}
                </p>
              </>
            ) : (
              <div className="dash-next-big mono" style={{ fontSize: 20, opacity: 0.6 }}>No upcoming departures</div>
            )}
            <div className="dash-next-rows">
              {nextDepartures.map((t) => (
                <div className="dash-next-row" key={t.id}>
                  <span className="mono">{fmtTime(t.scheduled_start)}</span>
                  <span>{t.vehicle_label || "—"} {t.route_short_name || t.headsign || ""}</span>
                  <span className="tag sev-ok">Scheduled</span>
                </div>
              ))}
              {nextDepartures.length === 0 && (
                <div style={{ padding: "8px 0", opacity: 0.5, fontSize: 12 }}>No scheduled trips remaining today.</div>
              )}
            </div>
          </div>

          {/* Incidents — dynamic */}
          <div className="card dash-card">
            <div className="dash-card-head">
              <h2 className="dash-card-title">
                Incidents &middot; <span className="dash-crit-count">{activeAlerts.length} ACTIVE</span>
              </h2>
              <a className="link" href="#/panic">All &rarr;</a>
            </div>
            <div className="dash-incidents">
              {activeAlerts.length === 0 && (
                <div style={{ padding: "20px 0", textAlign: "center", color: "var(--text-4)", fontSize: 13 }}>
                  No active incidents.
                </div>
              )}
              {activeAlerts.slice(0, 3).map((alert) => (
                <div className="dash-incident" key={alert.id}>
                  <div className="dash-incident-head">
                    <span className={`dot ${severityDot(alert.severity)}`} />
                    <span className="dash-incident-title">{alert.header_text}</span>
                    <span className="dash-incident-ago">{timeAgo(alert.created_at)}</span>
                  </div>
                  <p className="dash-incident-desc">{alert.description_text || "No description."}</p>
                  <div className="dash-incident-actions">
                    <button className="btn primary dash-pill-sm">Acknowledge</button>
                    <button className="btn ghost dash-pill-sm">Locate</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
